import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { Alert, Badge, Button, Form, Image, ListGroup, Modal } from 'react-bootstrap'
import moment from 'moment-timezone'
import { upperCaseFirst } from 'upper-case-first'
import FontAwesomeIcon from '@severed-links/common.font-awesome-icon'
import { getReportedItems, resolveReportedItem } from '@severed-links/common.redherrings-reducers/reporting'
import { createNotification } from '@severed-links/common.redherrings-reducers/notifications'
import { playerUrl } from '@severed-links/common.redherrings-constants'
import * as s from './Moderation.scss'

const STATUSES = ['Open', 'Reviewing', 'Actioned', 'Dismissed', 'Withdrawn']
const RESOLUTION_ACTIONS = [
    { value: 'ContentRemoved', label: 'Remove reported content now' },
    { value: 'UserWarnedSeparately', label: 'User warned separately' },
    { value: 'UserAccessRemovedSeparately', label: 'User access removed separately' },
    { value: 'NoViolation', label: 'No violation found' },
    { value: 'OtherActionCompletedSeparately', label: 'Other action completed separately' },
]

const actionErrorMessage = action => action.payload?.response?.message || action.payload?.message || 'The report could not be resolved.'

const itemTypeLabel = itemType => itemType === 'RecapComment' ? 'Recap Comment' : (itemType || 'Unknown item')

const PlayerDetail = ({ player = {}, fallback = 'Unknown user' }) => (
    <div className={s.player}>
        <Image src={playerUrl(player.avatarUrl)} roundedCircle />
        <div>
            <strong>{player.firstName || player.lastName ? `${player.firstName || ''} ${player.lastName || ''}`.trim() : fallback}</strong>
            {player.userName ? <small>{player.userName}</small> : null}
        </div>
    </div>
)

const ReportedItems = () => {
    const dispatch = useDispatch()
    const { appName = '' } = useParams()
    const app = upperCaseFirst(appName)
    const { reportedItems = [], isFetchingReportedItems, isSavingReport } = useSelector(state => state.reporting)
    const [status, setStatus] = useState('Open')
    const [activeReport, setActiveReport] = useState(null)
    const [resolutionStatus, setResolutionStatus] = useState('Actioned')
    const [resolutionAction, setResolutionAction] = useState('ContentRemoved')
    const [resolutionNotes, setResolutionNotes] = useState('')
    const requiresExternalActionNotes = resolutionStatus === 'Actioned' && resolutionAction !== 'ContentRemoved'

    useEffect(() => { dispatch(getReportedItems(app, status)) }, [app, status])

    const openResolution = report => {
        setActiveReport(report)
        setResolutionStatus('Actioned')
        setResolutionAction('ContentRemoved')
        setResolutionNotes('')
    }

    const saveResolution = () => {
        dispatch(resolveReportedItem(activeReport._id, {
            status: resolutionStatus,
            resolutionAction: resolutionStatus === 'Dismissed' ? 'NoViolation' : resolutionAction,
            resolutionNotes,
        })).then(action => {
            if (action.error || action.payload?.error || action.payload?.response?.error) {
                dispatch(createNotification({ headline: 'Resolve Report', message: actionErrorMessage(action), type: 'error', timeout: 6000 }))
                return
            }
            dispatch(createNotification({ headline: 'Resolve Report', message: action.payload?.message || 'Report resolved.', type: 'success', timeout: 4000 }))
            setActiveReport(null)
        })
    }

    return (
        <div className={`${s.container} ${s[appName.toLowerCase()] || ''}`}>
            <div className={s.heading}>
                <div>
                    <h2 className={s.title}>Reported Items</h2>
                    <p className={s.subtitle}>Review and resolve user-generated content reports for {app}.</p>
                </div>
                <Form.Select aria-label='Report status' value={status} onChange={e => setStatus(e.target.value)}>
                    {STATUSES.map(item => <option value={item} key={item}>{item}</option>)}
                </Form.Select>
            </div>
            {!isFetchingReportedItems && !reportedItems.length ?
            <Alert variant='light'>There are no {status.toLowerCase()} reports for {app}.</Alert>
            : null}
            <ListGroup className={s.reportList}>
                {reportedItems.map(item => (
                    <ListGroup.Item key={item._id}>
                        <div className={s.reportHeading}>
                            <div>
                                <Badge bg='danger'>{item.reason}</Badge>
                                <Badge bg='secondary'>{item.app}</Badge>
                                <Badge bg='light' text='dark'>{itemTypeLabel(item.itemType)}</Badge>
                            </div>
                            <span>{moment(item.createdAt).fromNow()}</span>
                        </div>
                        <div className={s.itemLocation}>
                            <strong>
                                {item.metadataSnapshot?.recapTitle || 'Reported content'}
                                {item.metadataSnapshot?.recapYear ? ` (${item.metadataSnapshot.recapYear})` : ''}
                            </strong>
                            <small>Item ID: {item.itemId}</small>
                        </div>
                        <blockquote>{item.contentSnapshot || '[No text snapshot]'}</blockquote>
                        <div className={s.reportPeople}>
                            <div><small>Reported user</small><PlayerDetail player={item.reportedPlayer} fallback={item.metadataSnapshot?.authorName} /></div>
                            <div><small>Reported by</small><PlayerDetail player={item.reportingPlayer} /></div>
                        </div>
                        {item.details ? <p className={s.details}>{item.details}</p> : null}
                        <div className={s.reportFooter}>
                            <small>Report ID: {item._id}</small>
                            <Button onClick={() => openResolution(item)} disabled={isSavingReport}>
                                <FontAwesomeIcon name='check' /> Resolve
                            </Button>
                        </div>
                    </ListGroup.Item>
                ))}
            </ListGroup>

            <Modal show={!!activeReport} onHide={() => setActiveReport(null)} centered>
                <Modal.Header closeButton><Modal.Title>Resolve reported item</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className='mb-3'>
                        <Form.Label>Decision</Form.Label>
                        <Form.Select value={resolutionStatus} onChange={e => setResolutionStatus(e.target.value)}>
                            <option value='Actioned'>Take action</option>
                            <option value='Dismissed'>Dismiss report</option>
                        </Form.Select>
                    </Form.Group>
                    {resolutionStatus === 'Actioned' ?
                    <Form.Group className='mb-3'>
                        <Form.Label>Action taken</Form.Label>
                        <Form.Select value={resolutionAction} onChange={e => setResolutionAction(e.target.value)}>
                            {RESOLUTION_ACTIONS.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
                        </Form.Select>
                        <Form.Text muted>{resolutionAction === 'ContentRemoved' ?
                            'Saving will delete this reported comment immediately.' :
                            'Select this only after completing the action through the appropriate administrator tool.'}</Form.Text>
                    </Form.Group>
                    : null}
                    <Form.Group>
                        <Form.Label>Resolution notes</Form.Label>
                        <Form.Control as='textarea' rows={4} value={resolutionNotes} onChange={e => setResolutionNotes(e.target.value)} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='light' onClick={() => setActiveReport(null)}>Cancel</Button>
                    <Button onClick={saveResolution} disabled={isSavingReport || (requiresExternalActionNotes && !resolutionNotes.trim())}>
                        {isSavingReport ? 'Saving...' : 'Save resolution'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default ReportedItems
